import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/tickets.css';

const API = process.env.REACT_APP_BACKEND_URL;

export function useActiveTicketRound() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/tickets/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => { if (!cancelled) { setData(d && d.active ? d : null); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { round: data, loading };
}

// Mystery taglines for ticket round - hide exact sold/remaining numbers
const TICKET_TAGLINES_BANNER = [
  { icon: '🔥', text: 'Heiße Phase – sichere dir deine Chance!' },
  { icon: '⏳', text: 'Soeben hat jemand Lose gekauft' },
  { icon: '🎯', text: 'Verkauf laeuft auf Hochtouren' },
  { icon: '🚀', text: 'Werde Teil der Verlosung' },
  { icon: '✨', text: 'Glücksmomente warten auf dich' },
];

function useRotatingTicketTag(intervalMs = 4500) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * TICKET_TAGLINES_BANNER.length));
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % TICKET_TAGLINES_BANNER.length), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return TICKET_TAGLINES_BANNER[idx];
}

/** Compact hero banner used on shop landing pages. */
export function TicketHeroStrip() {
  const { round } = useActiveTicketRound();
  const tagline = useRotatingTicketTag();
  if (!round) return null;
  return (
    <div className="ticket-hero-strip" data-testid="ticket-hero-strip">
      <div className="ths-content">
        <span className="ths-tag">{round.category_label || 'Verlosung'}</span>
        <h2>{round.prize_title || round.title}</h2>
        <p>{round.subtitle || `Sichere dir deine Chance ab ${Number(round.ticket_price || 2).toFixed(2).replace('.', ',')} € pro Los – jeder Kauf zählt!`}</p>
        <div className="ths-mystery">
          <span className="ths-mystery-icon">{tagline.icon}</span>
          <span className="ths-mystery-text">{tagline.text}</span>
        </div>
      </div>
      <Link to="/lose" className="ths-cta" data-testid="ticket-hero-cta">{round.cta_label || 'Lose sichern'} →</Link>
    </div>
  );
}

/** Cart cross-sell card "Möchtest du auch Lose mitnehmen?" */
export function TicketCartOffer({ onAdded }) {
  const { round } = useActiveTicketRound();
  const [busy, setBusy] = useState(false);
  if (!round) return null;
  const price = Number(round.ticket_price || 2);

  const add = async (qty) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/tickets/add-to-cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity: qty, round_slug: round.slug }),
      });
      if (r.ok && onAdded) onAdded(qty);
    } catch (e) {} // ignore
    setBusy(false);
  };

  return (
    <div className="ticket-offer-card" data-testid="ticket-cart-offer">
      <div className="toc-emoji">🎟️</div>
      <div className="toc-body">
        <strong>Schon dabei? Mach mit beim Projektauto!</strong>
        <p>{round.prize_title || round.title}. Jedes Los kostet nur <strong>{price.toFixed(2).replace('.', ',')} €</strong>.</p>
        <div className="toc-actions">
          <button disabled={busy} onClick={() => add(1)} data-testid="add-1-ticket">+1 Los · {price.toFixed(2).replace('.', ',')} €</button>
          <button disabled={busy} onClick={() => add(5)} data-testid="add-5-tickets">+5 Lose · {(price * 5).toFixed(2).replace('.', ',')} €</button>
          <button disabled={busy} onClick={() => add(10)} data-testid="add-10-tickets" className="toc-highlight">+10 Lose · {(price * 10).toFixed(2).replace('.', ',')} €</button>
          <Link to="/lose" className="toc-more">Mehr erfahren</Link>
        </div>
      </div>
    </div>
  );
}

/** Slim inline pill rendered on the success page. */
export function TicketSuccessMention() {
  const { round } = useActiveTicketRound();
  if (!round) return null;
  return (
    <div className="ticket-success-mention" data-testid="ticket-success-mention">
      <span className="tsm-emoji">🎟️</span>
      <div>
        <strong>Lust auf mehr Glück?</strong>
        <div>Hol dir Lose für unser Projektauto – ab {Number(round.ticket_price || 2).toFixed(2).replace('.', ',')} €.</div>
      </div>
      <Link to="/lose">Lose sichern →</Link>
    </div>
  );
}

/** Compact dual-promo box (Lossystem + Gewinne deine Bestellung) for product pages, sidebar etc. */
export function RafflePromoMini() {
  const { round: ticketRound } = useActiveTicketRound();
  const [raffle, setRaffle] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/raffle/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => setRaffle(d && d.active ? d : null))
      .catch(() => {});
  }, []);
  if (!ticketRound && !raffle) return null;

  return (
    <div className="raffle-promo-mini" data-testid="raffle-promo-mini">
      {raffle && (
        <Link to="/gewinnspiel" className="rpm-card rpm-raffle" data-testid="rpm-raffle-card">
          <div className="rpm-icon">🎁</div>
          <div className="rpm-body">
            <div className="rpm-label">Jede Bestellung gewinnt</div>
            <strong>Gewinne deine Bestellung zurück</strong>
            <span className="rpm-sub">Auto-Teilnahme · 5 € Trostpreis garantiert</span>
          </div>
          <span className="rpm-arrow">→</span>
        </Link>
      )}
      {ticketRound && (
        <Link to="/lose" className="rpm-card rpm-ticket" data-testid="rpm-ticket-card">
          <div className="rpm-icon">🎟️</div>
          <div className="rpm-body">
            <div className="rpm-label">{ticketRound.category_label || 'Verlosung'}</div>
            <strong>{ticketRound.prize_title || ticketRound.title}</strong>
            <span className="rpm-sub">Lose ab {Number(ticketRound.ticket_price || 2).toFixed(2).replace('.', ',')} € · Verkauf läuft</span>
          </div>
          <span className="rpm-arrow">→</span>
        </Link>
      )}
    </div>
  );
}

/** Floating sticky bottom-bar advertising both raffles. Auto-dismissable. */
export function RaffleStickyBar() {
  const { round: ticketRound } = useActiveTicketRound();
  const [raffle, setRaffle] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('raffle-sticky-dismissed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    fetch(`${API}/api/raffle/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => setRaffle(d && d.active ? d : null))
      .catch(() => {});
  }, []);
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('raffle-sticky-dismissed', '1'); } catch {}
  };
  if (dismissed) return null;
  if (!ticketRound && !raffle) return null;
  const ticketPrice = ticketRound ? Number(ticketRound.ticket_price || 2).toFixed(2).replace('.', ',') : '2,00';
  return (
    <div className="raffle-sticky-bar" data-testid="raffle-sticky-bar">
      <span className="rsb-emoji">🎁</span>
      <div className="rsb-text">
        {ticketRound ? (
          <>
            <strong>{ticketRound.prize_title || ticketRound.category_label || 'Verlosung'}</strong> · Lose ab {ticketPrice} € · jede Bestellung gewinnt mit
          </>
        ) : (
          <>Jede Bestellung nimmt automatisch am Gewinnspiel teil!</>
        )}
      </div>
      <Link to={ticketRound ? '/lose' : '/gewinnspiel'} className="rsb-cta">Mehr erfahren</Link>
      <button className="rsb-close" onClick={dismiss} aria-label="Schliessen" data-testid="raffle-sticky-close">×</button>
    </div>
  );
}
